import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { MetaAppFormData, MetaAppPurpose } from '../types/meta.types';
import { InfoIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const PURPOSE_OPTIONS: { value: MetaAppPurpose; label: string; description: string }[] = [
    { value: 'both', label: 'Ambos', description: 'Pode ser usado tanto em broadcast quanto em funil.' },
    { value: 'broadcast', label: 'Broadcast', description: 'Só é elegível para envio em massa (broadcast).' },
    { value: 'funnel', label: 'Funil', description: 'Só é elegível para resposta automática de funil.' },
];

const REQUIRED_SCOPES = [
    { scope: 'pages_show_list', label: 'Listar páginas', description: 'Permite escolher quais páginas conectar durante a autorização.' },
    { scope: 'pages_messaging', label: 'Mensagens da página', description: 'Envia e recebe mensagens diretas (essencial para o atendimento).' },
    { scope: 'pages_manage_metadata', label: 'Webhooks da página', description: 'Inscreve a página em webhooks para receber eventos em tempo real.' },
    { scope: 'pages_utility_messaging', label: 'Utility Messaging', description: 'Permite enviar mensagens fora da janela de 24h como mensagem utilitária (requer aprovação no Meta).' },
    { scope: 'business_management', label: 'Gerenciador de Negócios', description: 'Acessa ativos do Business Manager. Necessário para fluxos que tocam em ativos compartilhados.' },
    { scope: 'pages_read_engagement', label: 'Ler engajamento', description: 'Lê comentários e interações das páginas. Marque se o bot precisa monitorar posts.' },
    { scope: 'pages_read_user_content', label: 'Ler conteúdo de usuários', description: 'Lê o conteúdo publicado por usuários nas páginas (posts, comentários longos).' },
    { scope: 'pages_manage_posts', label: 'Gerenciar publicações', description: 'Cria, edita e apaga publicações da página.' },
    { scope: 'pages_manage_engagement', label: 'Responder a comentários', description: 'Responde e oculta comentários. Marque se o bot interage com publicações.' },
] as const;

interface CreateEditAppDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    formData: MetaAppFormData;
    onFormChange: (data: MetaAppFormData) => void;
    onSubmit: (e: React.FormEvent) => void;
    isLoading: boolean;
    isEdit?: boolean;
}

export default function CreateEditAppDialog({
                                                open,
                                                onOpenChange,
                                                formData,
                                                onFormChange,
                                                onSubmit,
                                                isLoading,
                                                isEdit = false,
                                            }: CreateEditAppDialogProps) {
    const redirectUri = import.meta.env.VITE_META_REDIRECT_URI || 'https://meta-api.rocketgroup.io/api/v1/meta/oauth/callback';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        {isEdit ? 'Editar App' : 'Criar App Personalizada'}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        {isEdit
                            ? 'Atualize as informações da sua aplicação Meta.'
                            : 'Configure uma nova aplicação do Meta para autenticação personalizada.'}
                    </DialogDescription>
                </DialogHeader>

                {/* Guia de Configuração */}
                {!isEdit && (
                    <Alert className="border-[#7dd3fc] bg-[#7dd3fc]/5">
                        <InfoIcon className="h-4 w-4 text-[#0f172a]" />
                        <AlertDescription className="text-sm text-gray-700">
                            <strong className="font-semibold">Como obter as credenciais:</strong>
                            <ol className="mt-2 ml-4 list-decimal space-y-1">
                                <li>
                                    Acesse{' '}
                                    <a
                                        href="https://developers.facebook.com/apps"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[#0f172a] hover:underline font-medium"
                                    >
                                        Facebook Developers
                                    </a>
                                </li>
                                <li>Crie uma nova aplicação ou selecione uma existente</li>
                                <li>Vá em "Configurações" → "Básico" para obter App ID e Secret</li>
                                <li>
                                    Configure a URL de callback abaixo nas configurações do Facebook Login
                                </li>
                            </ol>
                        </AlertDescription>
                    </Alert>
                )}

                <form onSubmit={onSubmit} className="space-y-4">
                    {/* Nome da App */}
                    <div className="space-y-2">
                        <Label htmlFor="app_name" className="text-gray-700 font-medium">
                            Nome da App <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="app_name"
                            value={formData.app_name}
                            onChange={(e) =>
                                onFormChange({ ...formData, app_name: e.target.value })
                            }
                            placeholder="Minha App Personalizada"
                            required
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a]"
                        />
                    </div>

                    {/* App ID */}
                    <div className="space-y-2">
                        <Label htmlFor="app_id" className="text-gray-700 font-medium">
                            App ID <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="app_id"
                            value={formData.app_id}
                            onChange={(e) =>
                                onFormChange({ ...formData, app_id: e.target.value })
                            }
                            placeholder="123456789012345"
                            required
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono"
                        />
                        <p className="text-xs text-gray-500">
                            Encontrado em: Configurações → Básico
                            {isEdit && ' — atenção: alterar o App ID afeta a autenticação OAuth desta app.'}
                        </p>
                    </div>

                    {/* App Secret */}
                    <div className="space-y-2">
                        <Label htmlFor="app_secret" className="text-gray-700 font-medium">
                            App Secret <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="app_secret"
                            type="password"
                            value={formData.app_secret}
                            onChange={(e) =>
                                onFormChange({ ...formData, app_secret: e.target.value })
                            }
                            placeholder="••••••••••••••••••••••••••••••••"
                            required
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono"
                        />
                        <p className="text-xs text-gray-500">
                            Clique em "Mostrar" ao lado do App Secret para visualizar
                        </p>
                    </div>

                    {/* Redirect URI */}
                    <div className="space-y-2">
                        <Label htmlFor="redirect_uri" className="text-gray-700 font-medium">
                            URL de Callback <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="redirect_uri"
                            value={formData.redirect_uri || redirectUri}
                            onChange={(e) =>
                                onFormChange({ ...formData, redirect_uri: e.target.value })
                            }
                            placeholder={redirectUri}
                            required
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono text-sm"
                        />
                        <p className="text-xs text-gray-500">
                            Configure esta URL em: Produtos → Facebook Login → Configurações → URLs de redirecionamento válidas do OAuth
                        </p>
                    </div>

                    {/* Permissões */}
                    <div className="space-y-3">
                        <div>
                            <Label className="text-gray-700 font-medium">Permissões</Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Escopos que este app vai requisitar ao usuário no fluxo de OAuth do Meta.
                            </p>
                        </div>

                        {/* Obrigatórias */}
                        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                Obrigatório
                            </p>
                            {REQUIRED_SCOPES.map((item) => (
                                <div key={item.scope} className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked
                                        disabled
                                        readOnly
                                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 opacity-60 cursor-not-allowed"
                                        aria-label={item.label}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium text-gray-900">{item.label}</span>
                                            <code className="text-xs text-gray-400 font-mono">{item.scope}</code>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Propósito */}
                    <div className="space-y-2">
                        <Label htmlFor="purpose" className="text-gray-700 font-medium">
                            Propósito
                        </Label>
                        <Select
                            value={formData.purpose ?? 'both'}
                            onValueChange={(value: MetaAppPurpose) =>
                                onFormChange({ ...formData, purpose: value })
                            }
                        >
                            <SelectTrigger id="purpose" className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a]">
                                <SelectValue placeholder="Selecione o propósito" />
                            </SelectTrigger>
                            <SelectContent className="bg-white">
                                {PURPOSE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                            {PURPOSE_OPTIONS.find((o) => o.value === (formData.purpose ?? 'both'))?.description}
                            {' '}Restringe de verdade em quais tipos de envio esse app pode ser escolhido.
                        </p>
                    </div>

                    {/* API Version */}
                    <div className="space-y-2">
                        <Label htmlFor="api_version" className="text-gray-700 font-medium">
                            Versão da API
                        </Label>
                        <Input
                            id="api_version"
                            value={formData.api_version}
                            onChange={(e) =>
                                onFormChange({ ...formData, api_version: e.target.value })
                            }
                            placeholder="v18.0"
                            className="border-gray-300 focus:border-[#0f172a] focus:ring-[#0f172a] font-mono"
                        />
                        <p className="text-xs text-gray-500">Padrão: v18.0</p>
                    </div>

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isLoading}
                            className="border-gray-300"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                        >
                            {isLoading ? (
                                <>
                                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    {isEdit ? 'Salvando...' : 'Criando...'}
                                </>
                            ) : isEdit ? (
                                'Salvar Alterações'
                            ) : (
                                'Criar App'
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
